import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, ExternalLink, ChevronDown, Check, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { request } from "../service/HttpService";
import { sectionColor, paperLink } from "../service/Operations";

function pageWindows(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const show = new Set([1, total, current - 1, current, current + 1].filter(n => n >= 1 && n <= total));
    const sorted = [...show].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
        result.push(sorted[i]);
    }
    return result;
}

export default function SimilarWorks() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");

    const [papers, setPapers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [section, setSection] = useState("ALL");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 10;
    const dropdownRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target))
                setDropdownOpen(false);
        };
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);

    useEffect(() => {
        if (!id) { setLoading(false); return; }
        request("GET", `/similar/papers/${id}`)
            .then(res => {
                const list = res.similarPapers ?? [];
                setPapers(list);
                const uniqueSections = [...new Set(list.map(p => p.section).filter(Boolean))];
                setCategories(["ALL", ...uniqueSections]);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [id]);

    const filtered = (section === "ALL" ? papers : papers.filter(p => p.section === section))
        .filter(p => {
            if (!search) return true;
            const q = search.toLowerCase();
            return (p.articleTitle ?? "").toLowerCase().includes(q) || (p.authors ?? "").toLowerCase().includes(q);
        })
        .sort((a, b) => b.similarity - a.similarity);

    useEffect(() => { setPage(1); }, [search, section]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const cap = (s) => s === "ALL" ? "All" : s.charAt(0).toUpperCase() + s.slice(1);

    return (
        <div className="home-layout">
            <Sidebar activePage="Submissions" />

            <main className="home-main">
                <Header />

                <div className="home-content">
                    <button className="chpw-back" onClick={() => navigate(`/submission?id=${id}`)}>
                        <ArrowLeft size={14} />
                        Back to Submission
                    </button>

                    <div className="upload-page-title-row">
                        <div>
                            <h1 className="upload-page-heading">Similar Works</h1>
                            {!loading && <p className="upload-page-meta">{papers.length} paper{papers.length !== 1 ? "s" : ""} show similarity</p>}
                        </div>
                    </div>

                    <div className="submissions-filters" style={{ marginBottom: 16 }}>
                        {/* Section filter dropdown */}
                        <div className="similar-works-filter" ref={dropdownRef}>
                            <div
                                className="similar-works-dropdown"
                                role="button"
                                tabIndex={0}
                                aria-haspopup="listbox"
                                aria-expanded={dropdownOpen}
                                onClick={() => setDropdownOpen(o => !o)}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDropdownOpen(o => !o); }
                                    if (e.key === "Escape") setDropdownOpen(false);
                                }}
                            >
                                <span className="similar-works-dropdown-label">Section: {cap(section)}</span>
                                <ChevronDown size={14} className={`similar-works-chevron${dropdownOpen ? " open" : ""}`} />

                                {dropdownOpen && (
                                    <ul
                                        className="similar-works-dropdown-menu"
                                        role="listbox"
                                        onClick={e => e.stopPropagation()}
                                    >
                                        {categories.map(cat => (
                                            <li
                                                key={cat}
                                                role="option"
                                                aria-selected={section === cat}
                                                className={`similar-works-dropdown-item${section === cat ? " active" : ""}`}
                                                onClick={() => { setSection(cat); setDropdownOpen(false); }}
                                            >
                                                <span style={{ textTransform: "capitalize" }}>{cap(cat)}</span>
                                                {section === cat && <Check size={13} />}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        <div className="submissions-search-wrap" style={{ position: "relative", marginLeft: "auto" }}>
                            <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }} />
                            <input
                                type="text"
                                placeholder="Search…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ paddingLeft: 30, paddingRight: search ? 26 : 10, paddingTop: 7, paddingBottom: 7, border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: "var(--surface-2)", outline: "none", width: 160 }}
                            />
                            {search && (
                                <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--ink-300)", fontSize: 16, lineHeight: 1 }}>×</button>
                            )}
                        </div>
                    </div>

                    {!loading && totalPages > 1 && (
                        <div className="pagination-bar" style={{ marginBottom: 12 }}>
                            <p className="pagination-count">{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}</p>
                            <div className="pagination-controls">
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === 1 ? "var(--surface-3)" : "var(--surface)", cursor: safePage === 1 ? "default" : "pointer", fontSize: 12, color: safePage === 1 ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><ChevronLeft size={13} /><span className="pagination-btn-text">Previous</span></button>
                                <div className="pagination-pages">
                                    {pageWindows(safePage, totalPages).map((n, i) =>
                                        n === null
                                            ? <span key={`e${i}`} style={{ padding: "5px 2px", fontSize: 12, color: "var(--ink-300)" }}>…</span>
                                            : <button key={n} onClick={() => setPage(n)} style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)", background: n === safePage ? "var(--brand)" : "var(--surface)", color: n === safePage ? "white" : "var(--ink-700)", cursor: "pointer", fontSize: 12, fontWeight: n === safePage ? 600 : 400 }}>{n}</button>
                                    )}
                                </div>
                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === totalPages ? "var(--surface-3)" : "var(--surface)", cursor: safePage === totalPages ? "default" : "pointer", fontSize: 12, color: safePage === totalPages ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><span className="pagination-btn-text">Next</span><ChevronRight size={13} /></button>
                            </div>
                            <div className="pagination-spacer" />
                        </div>
                    )}

                    <div className="submissions-card">
                        <table className="submissions-table similar-works-table">
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th style={{ whiteSpace: "nowrap" }}>% Overlap</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <tr key={i}>
                                            <td>
                                                <div className="skeleton" style={{ height: 12, width: "72%", marginBottom: 7 }} />
                                                <div className="skeleton" style={{ height: 10, width: "48%" }} />
                                            </td>
                                            <td><div className="skeleton" style={{ height: 12, width: 36, borderRadius: 6 }} /></td>
                                        </tr>
                                    ))
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={2}>
                                            <p className="settings-empty">No similar papers found.</p>
                                        </td>
                                    </tr>
                                ) : paginated.map(p => (
                                    <tr key={p.id}>
                                        <td>
                                            {paperLink(p) ? (
                                                <a
                                                    href={paperLink(p)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="similar-works-title-link"
                                                >
                                                    {p.articleTitle}
                                                    <ExternalLink size={11} className="similar-works-ext-icon" />
                                                </a>
                                            ) : (
                                                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-900)", lineHeight: 1.4 }}>
                                                    {p.articleTitle}
                                                </span>
                                            )}
                                            {p.section && (
                                                <div style={{ marginTop: 5 }}>
                                                    <span className="similar-works-section-badge" style={sectionColor(p.section)}>{cap(p.section)}</span>
                                                </div>
                                            )}
                                            {p.authors && <p className="submission-authors" style={{ marginTop: 4 }}>{p.authors}</p>}
                                        </td>
                                        <td className="similar-works-pct">{Math.round(p.similarity * 100)}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
}
